import { useEffect, useState, useCallback } from "react";
import { useFetch } from "use-http";
import _get from "lodash/get";
import useCollapse from "react-collapsed";
import Skeleton from "react-loading-skeleton";

import Icon from "~/components/shared/Icon";
import { Execution } from "~/components/analytics/shared/type";

type Props = {
  autoExpand?: boolean;
  endpoint: string;
  failureReason: Execution["failureReason"];
  failureExpanded?: Execution["failureExpanded"];
};

const FailureReason = ({ autoExpand, endpoint, failureReason, failureExpanded }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { get, response, loading, error } = useFetch(endpoint);
  const [expandedFailureReason, setExpandedFailureReason] =
    useState<Execution["failureExpanded"]>(failureExpanded);
  const { getCollapseProps } = useCollapse({ isExpanded });

  const fetchExpandedFailureReason = useCallback(async () => {
    if (expandedFailureReason) {
      return;
    }

    const failureExpanded = await get();
    if (response.ok) {
      setExpandedFailureReason(failureExpanded);
    }
  }, [get, response, expandedFailureReason]);

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
    fetchExpandedFailureReason();
  };

  useEffect(() => {
    if (autoExpand) {
      setIsExpanded(true);
      fetchExpandedFailureReason();
    }
  }, []);

  if (!failureReason) {
    return null;
  }

  return (
    <div className="terminal-display bg-charcoal-700 relative rounded">
      <pre className="block overflow-hidden whitespace-pre-wrap break-words font-mono text-xs font-normal leading-5 text-gray-500">
        <div className={`${!isExpanded ? "max-w-full truncate" : ""} relative px-4 py-2 pl-12`}>
          <button
            className="no-title-tooltip absolute inset-0 w-full cursor-pointer border-none bg-none pl-5 text-left filter transition duration-200 ease-in-out hover:brightness-150"
            aria-label={isExpanded ? "Hide failure reason" : "Show failure reason"}
            onClick={toggleExpand}
          >
            <Icon
              icon={isExpanded ? "thick-minus" : "thick-plus"}
              className="absolute top-3"
              style={{ width: "10px", height: "10px" }}
            />
          </button>

          {failureReason}
        </div>

        <div data-testid="expanded-failure-reason" {...getCollapseProps()}>
          <div className="mt-2 px-4 py-2 pl-12">
            {expandedFailureReason &&
              expandedFailureReason
                .map((failure) => {
                  const expandedString = _get(failure, "expanded", []).join("\n");
                  const backtraceString = _get(failure, "backtrace", []).join("\n");
                  return expandedString.concat("\n", backtraceString);
                })
                .join("\n\n")}
            {loading && (
              <Skeleton
                containerTestId="loading-skeleton"
                width="80%"
                baseColor="#444444"
                highlightColor="#5D5D5D"
                count={6}
              />
            )}
            {error && "Error: test failure reasons could not be loaded"}
          </div>
        </div>
      </pre>
    </div>
  );
};

export default FailureReason;
